import { ReactNode } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { neutral } from '../../theme/colors';
import { palette } from './report-theme';

interface KpiCardProps {
	label: string;
	value: string;
	/** Percentage change against the comparison period. */
	deltaPercent?: number;
	/** What the delta is measured against, e.g. "vs previous period". */
	deltaCaption?: string;
	/** Secondary line under the value — a split, a rate, a count. */
	footnote?: ReactNode;
	/** Sparkline values, oldest first. Drawn only when there are 2+ points. */
	spark?: number[];
	/** A higher number is worse for this metric (e.g. discount given). */
	invertDelta?: boolean;
	/** Explains where the number comes from, on hover. */
	help?: string;
}

const sparkOptions = (color: string): ApexOptions => ({
	chart: {
		type: 'area',
		sparkline: { enabled: true },
		animations: { enabled: false },
		parentHeightOffset: 0,
	},
	stroke: { curve: 'smooth', width: 2 },
	fill: {
		type: 'gradient',
		gradient: { shadeIntensity: 0, opacityFrom: 0.22, opacityTo: 0, stops: [0, 100] },
	},
	colors: [color],
	tooltip: { enabled: false },
	dataLabels: { enabled: false },
});

export const KpiCard = ({
	label,
	value,
	deltaPercent,
	deltaCaption = 'vs previous period',
	footnote,
	spark,
	invertDelta = false,
	help,
}: KpiCardProps) => {
	const hasDelta = deltaPercent !== undefined && Number.isFinite(deltaPercent);
	const isFlat = hasDelta && Math.abs(deltaPercent as number) < 0.05;
	const isGood = hasDelta && (invertDelta ? (deltaPercent as number) < 0 : (deltaPercent as number) > 0);
	const deltaColor = !hasDelta || isFlat
		? neutral[500]
		: isGood
			? palette.status.positive
			: palette.status.critical;
	const DeltaIcon = !hasDelta || isFlat
		? RemoveRoundedIcon
		: (deltaPercent as number) > 0
			? ArrowUpwardRoundedIcon
			: ArrowDownwardRoundedIcon;

	const card = (
		<Card
			elevation={0}
			sx={{
				height: '100%',
				border: `1px solid ${neutral[200]}`,
				borderRadius: 2,
				overflow: 'hidden',
			}}
		>
			<CardContent sx={{ p: 2.5, pb: spark && spark.length > 1 ? 0 : 2.5, '&:last-child': { pb: spark && spark.length > 1 ? 0 : 2.5 } }}>
				<Typography
					variant="overline"
					sx={{ color: 'text.secondary', letterSpacing: '0.06em', lineHeight: 1.6 }}
				>
					{label}
				</Typography>
				<Typography variant="h4" sx={{ mt: 0.5, fontWeight: 600, lineHeight: 1.2 }}>
					{value}
				</Typography>

				<Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, minHeight: 22 }}>
					{hasDelta && (
						<>
							<Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: deltaColor }}>
								<DeltaIcon sx={{ fontSize: 16 }} />
								<Typography variant="caption" sx={{ fontWeight: 600, color: deltaColor }}>
									{`${Math.abs(deltaPercent as number).toFixed(1)}%`}
								</Typography>
							</Stack>
							<Typography variant="caption" color="text.secondary">
								{deltaCaption}
							</Typography>
						</>
					)}
					{!hasDelta && footnote && (
						<Typography variant="caption" color="text.secondary">
							{footnote}
						</Typography>
					)}
				</Stack>

				{hasDelta && footnote && (
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
						{footnote}
					</Typography>
				)}

				{spark && spark.length > 1 && (
					<Box sx={{ mx: -2.5, mt: 1 }}>
						<ReactApexChart
							type="area"
							height={48}
							options={sparkOptions(palette.single)}
							series={[{ name: label, data: spark }]}
						/>
					</Box>
				)}
			</CardContent>
		</Card>
	);

	return help ? (
		<Tooltip title={help} placement="top" arrow>
			<Box sx={{ height: '100%' }}>{card}</Box>
		</Tooltip>
	) : (
		card
	);
};
